import React from 'react';

import Link from 'next/link';

import WhatsAppIcon from '@/assets/icons/WhatsAppIcon';
import { resolveMarketingUrl } from '@/constants/routes.mapping';
import {
  ContactInfo,
  FooterSection,
  formatContactValue,
  getContactHref,
} from '@/data/footerData';

interface FooterContactSectionProps {
  section: FooterSection;
}

export const FooterContactSection: React.FC<FooterContactSectionProps> = ({
  section,
}) => {
  if (!section.contactInfo) return null;

  const renderContactItem = (info: ContactInfo, index: number) => {
    const values = formatContactValue(info);
    const href = getContactHref(info);

    const getHrefForIndex = (idx: number): string | undefined => {
      if (!href) return undefined;
      if (Array.isArray(href)) {
        return href[idx];
      }
      return idx === 0 ? href : undefined;
    };

    return (
      <div
        key={`${info.type}-${info.order || index}-${index}`}
        className="mb-3 flex min-w-0 items-start gap-3"
      >
        {info.icon && (
          <div className="mt-0.5 h-5 w-5 shrink-0 text-gray-300">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d={info.icon}
              />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          {info.label && (
            <div className="mb-1 text-sm font-medium text-gray-300">
              {info.label}
            </div>
          )}
          <div className="text-sm wrap-anywhere wrap-break-word text-gray-200">
            {values.map((value, idx) => {
              const itemHref = getHrefForIndex(idx);
              const isExternal = itemHref?.startsWith('http');

              if (
                info.type === 'hours' &&
                typeof value === 'string' &&
                value.includes(':')
              ) {
                const colonIdx = value.indexOf(':');
                const days = value.substring(0, colonIdx).trim();
                const time = value.substring(colonIdx + 1).trim();
                return (
                  <div key={idx} className="mb-2 last:mb-0">
                    <span className="block font-medium text-white">{days}</span>
                    <span className="mt-0.5 block text-gray-300">{time}</span>
                  </div>
                );
              }

              // Distinct accessible name from the header's matching call/
              // WhatsApp links (same numbers, different page region) — both
              // otherwise expose the same href with a generic name, which
              // Lighthouse's "identical links" check (rightly) flags as
              // ambiguous for screen-reader users navigating link-by-link.
              const contactAriaLabel = itemHref?.startsWith('tel:')
                ? `Call us in the footer at ${value}`
                : itemHref?.startsWith('https://wa.me')
                  ? `Message us on WhatsApp in the footer at ${value}`
                  : undefined;

              const isWhatsAppLink =
                typeof itemHref === 'string' &&
                itemHref.startsWith('https://wa.me');

              return (
                <div key={idx}>
                  {itemHref ? (
                    <Link
                      href={resolveMarketingUrl(itemHref) || itemHref}
                      // py-1.5 widens the tap target closer to the 24px
                      // minimum touch-target guideline — these were plain
                      // inline text links with no vertical padding before.
                      className={`inline-flex items-center py-1.5 wrap-anywhere wrap-break-word transition-colors hover:text-white ${
                        isWhatsAppLink ? '-ml-8 gap-3' : 'gap-1.5'
                      }`}
                      aria-label={contactAriaLabel}
                      {...(isExternal
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      {isWhatsAppLink && (
                        <WhatsAppIcon className="h-4.5 w-4.5" />
                      )}
                      <span>{value}</span>
                    </Link>
                  ) : (
                    <span>{value}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-w-0 space-y-4">
      {section.contactInfo.map(renderContactItem)}
    </div>
  );
};
